import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import '../styles/Login.css';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [redirectPath, setRedirectPath] = useState('/dashboard');
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, googleLogin, kakaoLogin, naverLogin, message, redirectPath: contextRedirectPath, error: authError, clearError } = useAuth();

  // formData 변경 감지
  useEffect(() => {
    console.log('formData 변경됨:', formData);
  }, [formData]);

  // AuthContext의 에러 상태 감지
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  // 리디렉션 경로 가져오기
  const getRedirectPath = () => {
    // URL 파라미터에서 리디렉션 경로 확인
    const params = new URLSearchParams(location.search);
    const redirectParam = params.get('redirect');
    
    // 이전 페이지 경로 (state로 전달된 경우)
    const fromPath = location.state?.from || '';
    
    // URL 파라미터 > state로 전달된 경로 > 저장된 경로 > 기본 경로(대시보드) 순으로 우선순위
    return redirectParam || fromPath || contextRedirectPath || '/dashboard';
  };

  // 컴포넌트 마운트 시 리디렉션 경로 저장
  useEffect(() => {
    // 폼 데이터 초기화
    setFormData({
      email: '',
      password: ''
    });
    
    // 리디렉션 경로 미리 저장
    const redirectTo = getRedirectPath();
    if (redirectTo && redirectTo !== '/dashboard') {
      setRedirectPath(redirectTo);
    }
    
    // URL 파라미터에서 메시지 확인
    const params = new URLSearchParams(location.search);
    const registerSuccess = params.get('registerSuccess');
    if (registerSuccess) {
      setSuccess('회원가입이 완료되었습니다. 로그인해주세요.');
    }
    
    const verifySuccess = params.get('verifySuccess');
    if (verifySuccess) {
      setSuccess('이메일 인증이 완료되었습니다. 로그인해주세요.');
    }
    
    const resetSuccess = params.get('resetSuccess');
    if (resetSuccess) {
      setSuccess('비밀번호가 성공적으로 재설정되었습니다. 로그인해주세요.');
    }
    
    // AuthContext에서 메시지 확인
    if (message) {
      setSuccess(message);
    }
    
    // 이미 로그인된 경우 리디렉션
    if (isAuthenticated) {
      navigate(getRedirectPath());
    }
  }, [isAuthenticated, navigate, location.search, message, contextRedirectPath]);

  const { email, password } = formData;

  const onChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const onSubmit = async e => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');
    clearError(); // AuthContext의 에러 상태도 초기화

    if (!email || !password) {
      setError('모든 필드를 입력해주세요.');
      setIsLoading(false);
      return;
    }

    try {
      const success = await login(email, password);
      if (success) {
        navigate(getRedirectPath());
      }
    } catch (err) {
      // 에러 메시지는 이미 AuthContext에서 처리되어 state에 저장됨
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Google 로그인 처리
  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setIsLoading(true);
      setError('');
      clearError(); // AuthContext의 에러 상태 초기화
      
      // 안전장치: 일정 시간 후에도 로딩 상태가 계속되면 자동으로 해제
      const safetyTimeout = setTimeout(() => {
        if (isLoading) {
          console.log('Google login timeout - resetting loading state');
          setError('구글 로그인 처리 중 시간이 초과되었습니다. 다시 시도해주세요.');
          setIsLoading(false);
        }
      }, 20000); // 20초 후 타임아웃
      
      console.log('Google Login Success - Token Response:', tokenResponse);
      
      try {
        // Google API에서 사용자 정보 가져오기
        console.log('Google Login - 사용자 정보 요청 시작');
        const userInfoResponse = await axios.get(
          'https://www.googleapis.com/oauth2/v3/userinfo',
          { headers: { Authorization: `Bearer ${tokenResponse.access_token}` } }
        );
        
        console.log('Google Login - 사용자 정보 응답:', userInfoResponse.data);
        const userData = userInfoResponse.data;
        
        if (!userData || !userData.email) {
          clearTimeout(safetyTimeout);
          console.error('Google Login - 사용자 정보 부족:', userData);
          setError('Google에서 사용자 정보를 가져올 수 없습니다. 다시 시도해주세요.');
          setIsLoading(false);
          return;
        }
        
        // 리디렉션 경로 미리 저장
        const redirectTo = getRedirectPath();
        console.log('Google Login - 리디렉션 경로:', redirectTo);
        
        // AuthContext의 googleLogin 함수 호출
        console.log('Google Login - AuthContext googleLogin 호출');
        const result = await googleLogin(tokenResponse.access_token, userData);
        
        clearTimeout(safetyTimeout);
        
        if (result.success) {
          // 로그인 성공
          console.log('Google Login - 성공, 리디렉션 경로로 이동:', redirectTo);
          navigate(redirectTo);
        } else if (result.isNewUser && result.needsAdditionalInfo) {
          // 새 회원이고 추가 정보가 필요한 경우
          console.log('Google Login - 새 회원, 추가 정보 필요');
          
          // Register 페이지로 이동하면서 소셜 로그인 데이터 전달
          navigate('/register', { 
            state: { 
              socialLoginData: result.partialData,
              from: location.pathname + location.search
            } 
          });
        } else {
          // 로그인 실패 (다른 방식으로 가입된 회원 등)
          console.error('Google Login - 실패:', result.message);
          setError(result.message || 'Google 계정으로 로그인하는데 실패했습니다. 다시 시도해주세요.');
        }
      } catch (err) {
        clearTimeout(safetyTimeout);
        console.error('Google login error:', err);
        console.error('Error details:', {
          response: err.response ? {
            status: err.response.status,
            data: err.response.data
          } : 'No response',
          request: err.request ? 'Request sent but no response' : 'No request',
          message: err.message
        });
        setError('Google 로그인 중 오류가 발생했습니다.');
      } finally {
        clearTimeout(safetyTimeout);
        setIsLoading(false);
      }
    },
    onError: (error) => {
      console.error('Google Login Error:', error);
      setError('Google 로그인 중 오류가 발생했습니다.');
      setIsLoading(false);
    },
    flow: 'implicit' // 명시적으로 implicit 플로우 사용
  });

  // 인스타그램 로그인 처리 (나중에 구현)
  const handleInstagramLogin = () => {
    setIsLoading(true);
    setError('');
    clearError();
    
    // 안전장치: 일정 시간 후에도 로딩 상태가 계속되면 자동으로 해제
    const safetyTimeout = setTimeout(() => {
      if (isLoading) {
        console.log('Instagram login timeout - resetting loading state');
        setError('인스타그램 로그인 처리 중 시간이 초과되었습니다. 다시 시도해주세요.');
        setIsLoading(false);
      }
    }, 20000);
    
    // 현재는 기능 구현 전이므로 메시지 표시
    clearTimeout(safetyTimeout);
    setError('인스타그램 로그인 기능은 현재 개발 중입니다.');
    setIsLoading(false);
  };

  // X(구 트위터) 로그인 처리 (나중에 구현)
  const handleTwitterLogin = () => {
    setIsLoading(true);
    setError('');
    clearError();
    
    // 안전장치: 일정 시간 후에도 로딩 상태가 계속되면 자동으로 해제
    const safetyTimeout = setTimeout(() => {
      if (isLoading) {
        console.log('Twitter login timeout - resetting loading state');
        setError('X 로그인 처리 중 시간이 초과되었습니다. 다시 시도해주세요.');
        setIsLoading(false);
      }
    }, 20000);
    
    // 현재는 기능 구현 전이므로 메시지 표시
    clearTimeout(safetyTimeout);
    setError('X(구 트위터) 로그인 기능은 현재 개발 중입니다.');
    setIsLoading(false);
  };

  // Kakao 로그인 처리
  /*
  const handleKakaoLogin = () => {
    setIsLoading(true);
    setError('');
    clearError(); // AuthContext의 에러 상태 초기화
    
    // Kakao SDK가 로드되었는지 확인
    if (!window.Kakao) {
      setError('카카오 SDK를 로드할 수 없습니다. 다시 시도해주세요.');
      setIsLoading(false);
      return;
    }
    
    // 안전장치: 일정 시간 후에도 로딩 상태가 계속되면 자동으로 해제
    const safetyTimeout = setTimeout(() => {
      if (isLoading) {
        console.log('Kakao login timeout - resetting loading state');
        setError('카카오 로그인 처리 중 시간이 초과되었습니다. 다시 시도해주세요.');
        setIsLoading(false);
      }
    }, 20000); // 20초 후 타임아웃
    
    // 카카오 로그인 요청
    window.Kakao.Auth.login({
      success: (authObj) => {
        clearTimeout(safetyTimeout);
        
        // 사용자 정보 요청
        window.Kakao.API.request({
          url: '/v2/user/me',
          success: async (res) => {
            const kakaoAccount = res.kakao_account;
            
            if (!kakaoAccount || !kakaoAccount.email) {
              setError('카카오 계정에서 이메일 정보를 가져올 수 없습니다. 이메일 제공에 동의해주세요.');
              setIsLoading(false);
              return;
            }
            
            // 이름 정보 가져오기
            let name = '';
            if (kakaoAccount.profile && kakaoAccount.profile.nickname) {
              name = kakaoAccount.profile.nickname;
            }
            
            // 이름 분리 (성과 이름)
            let firstName = '';
            let lastName = '';
            
            if (name) {
              const nameParts = name.split(' ');
              if (nameParts.length > 1) {
                lastName = nameParts[0];
                firstName = nameParts.slice(1).join(' ');
              } else {
                firstName = name;
                lastName = '';
              }
            }
            
            // 리디렉션 경로 미리 저장
            const redirectTo = getRedirectPath();
            console.log('Kakao Login - 리디렉션 경로:', redirectTo);
            
            try {
              // AuthContext의 kakaoLogin 함수 호출
              const result = await kakaoLogin(authObj.access_token, {
                email: kakaoAccount.email,
                firstName: firstName,
                lastName: lastName,
                kakaoId: res.id
              });
              
              if (result.success) {
                // 로그인 성공
                navigate(redirectTo);
              } else if (result.isNewUser && result.needsAdditionalInfo) {
                // 새 회원이고 추가 정보가 필요한 경우
                console.log('Kakao Login - 새 회원, 추가 정보 필요');
                
                // Register 페이지로 이동하면서 소셜 로그인 데이터 전달
                navigate('/register', { 
                  state: { 
                    socialLoginData: result.partialData,
                    from: location.pathname + location.search
                  } 
                });
              } else {
                // 로그인 실패 (다른 방식으로 가입된 회원 등)
                setError(result.message || '카카오 계정으로 로그인하는데 실패했습니다. 다시 시도해주세요.');
              }
            } catch (err) {
              console.error('Kakao login error:', err);
              setError('카카오 로그인 중 오류가 발생했습니다.');
            } finally {
              setIsLoading(false);
            }
          },
          fail: (error) => {
            clearTimeout(safetyTimeout);
            console.error('Kakao API request failed:', error);
            setError('카카오 계정 정보를 가져오는데 실패했습니다.');
            setIsLoading(false);
          }
        });
      },
      fail: (error) => {
        clearTimeout(safetyTimeout);
        // 사용자가 취소한 경우와 다른 오류를 구분
        if (error && error.error_code === 'access_denied') {
          console.log('Kakao login canceled by user');
          setError('카카오 로그인이 취소되었습니다.');
        } else {
          console.error('Kakao login failed:', error);
          setError('카카오 로그인에 실패했습니다.');
        }
        setIsLoading(false);
      }
    });
  };
  */

  // Naver 로그인 처리
  /*
  const handleNaverLogin = () => {
    setIsLoading(true);
    setError('');
    clearError(); // AuthContext의 에러 상태 초기화
    
    // 네이버 로그인 객체가 있는지 확인
    if (!window.naver || !window.naver.LoginWithNaverId) {
      setError('네이버 로그인 SDK를 로드할 수 없습니다. 다시 시도해주세요.');
      setIsLoading(false);
      return;
    }
    
    // 안전장치: 일정 시간 후에도 로딩 상태가 계속되면 자동으로 해제
    const safetyTimeout = setTimeout(() => {
      if (isLoading) {
        console.log('Naver login timeout - resetting loading state');
        setError('네이버 로그인 처리 중 시간이 초과되었습니다. 다시 시도해주세요.');
        setIsLoading(false);
      }
    }, 20000); // 20초 후 타임아웃
    
    // 네이버 로그인 인스턴스 초기화
    try {
      const naverLoginInstance = new window.naver.LoginWithNaverId({
        clientId: process.env.REACT_APP_NAVER_CLIENT_ID,
        callbackUrl: `${window.location.origin}/auth/naver/callback`,
        isPopup: true,
        loginButton: { color: 'green', type: 3, height: 60 }
      });
      
      naverLoginInstance.init();
      
      // 팝업 창으로 네이버 로그인 실행
      naverLoginInstance.getLoginStatus((status) => {
        clearTimeout(safetyTimeout);
        
        if (status) {
          // 이미 로그인된 상태
          const { email, name, id, accessToken } = naverLoginInstance.user;
          
          if (!email) {
            setError('네이버 계정에서 이메일 정보를 가져올 수 없습니다. 이메일 제공에 동의해주세요.');
            setIsLoading(false);
            return;
          }
          
          // 이름 분리 (성과 이름)
          let firstName = '';
          let lastName = '';
          
          if (name) {
            const nameParts = name.split(' ');
            if (nameParts.length > 1) {
              lastName = nameParts[0];
              firstName = nameParts.slice(1).join(' ');
            } else {
              firstName = name;
              lastName = '';
            }
          }
          
          // 리디렉션 경로 미리 저장
          const redirectTo = getRedirectPath();
          console.log('Naver Login - 리디렉션 경로:', redirectTo);
          
          // AuthContext의 naverLogin 함수 호출
          naverLogin(accessToken.accessToken, {
            email,
            firstName,
            lastName,
            naverId: id
          }).then(result => {
            if (result.success) {
              // 로그인 성공
              navigate(redirectTo);
            } else if (result.isNewUser && result.needsAdditionalInfo) {
              // 새 회원이고 추가 정보가 필요한 경우
              console.log('Naver Login - 새 회원, 추가 정보 필요');
              
              // Register 페이지로 이동하면서 소셜 로그인 데이터 전달
              navigate('/register', { 
                state: { 
                  socialLoginData: result.partialData,
                  from: location.pathname + location.search
                } 
              });
            } else {
              // 로그인 실패 (다른 방식으로 가입된 회원 등)
              setError(result.message || '네이버 계정으로 로그인하는데 실패했습니다. 다시 시도해주세요.');
            }
          }).catch(err => {
            console.error('Naver login error:', err);
            setError('네이버 로그인 중 오류가 발생했습니다.');
          }).finally(() => {
            setIsLoading(false);
          });
        } else {
          // 로그인 필요
          naverLoginInstance.authorize();
          
          // 팝업이 열리면 타임아웃 재설정 (팝업 닫힘 감지를 위해)
          const popupCheckInterval = setInterval(() => {
            // 팝업이 닫혔는지 확인
            if (!naverLoginInstance.popup || naverLoginInstance.popup.closed) {
              clearInterval(popupCheckInterval);
              if (isLoading) {
                console.log('Naver login popup closed');
                setError('네이버 로그인이 취소되었습니다.');
                setIsLoading(false);
              }
            }
          }, 1000);
          
          // 30초 후에도 인터벌이 정리되지 않았다면 정리
          setTimeout(() => {
            clearInterval(popupCheckInterval);
          }, 30000);
        }
      });
    } catch (error) {
      clearTimeout(safetyTimeout);
      console.error('Naver login initialization error:', error);
      setError('네이버 로그인 초기화 중 오류가 발생했습니다.');
      setIsLoading(false);
    }
  };
  */

  return (
    <div className="login-container">
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <div className="loading-text">처리 중입니다...</div>
        </div>
      )}
      <div className="login-form-container">
        <h2>로그인</h2>
        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}
        <form onSubmit={onSubmit} autoComplete="on">
          <div className="form-group">
            <label htmlFor="email">이메일</label>
            <input
              type="email"
              id="email"
              name="email"
              value={email}
              onChange={onChange}
              required
              autoComplete="username"
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">비밀번호</label>
            <input
              type="password"
              id="password"
              name="password"
              value={password}
              onChange={onChange}
              required
              autoComplete="current-password"
            />
          </div>
          <button 
            type="submit" 
            className={`btn btn-primary ${isLoading ? 'btn-loading' : ''}`} 
            disabled={isLoading}
          >
            {isLoading ? ' ' : '로그인'}
          </button>
        </form>
        
        <div className="social-login">
          <p>소셜 계정으로 로그인</p>
          <div className="social-buttons">
            <button 
              className={`btn-social btn-google ${isLoading ? 'btn-loading' : ''}`}
              onClick={handleGoogleLogin}
              disabled={isLoading}
            >
              {!isLoading && <><i className="fab fa-google"></i> Google</>}
            </button>
            <button 
              className={`btn-social btn-instagram ${isLoading ? 'btn-loading' : ''}`}
              onClick={handleInstagramLogin}
              disabled={isLoading}
            >
              {!isLoading && <><i className="fab fa-instagram"></i> Instagram</>}
            </button>
            <button 
              className={`btn-social btn-twitter ${isLoading ? 'btn-loading' : ''}`}
              onClick={handleTwitterLogin}
              disabled={isLoading}
            >
              {!isLoading && <div className="fa-x-twitter">X</div>}
            </button>
            {/* 카카오 로그인 버튼 주석 처리
            <button 
              className={`btn-social btn-kakao ${isLoading ? 'btn-loading' : ''}`}
              onClick={handleKakaoLogin}
              disabled={isLoading}
            >
              {!isLoading && <><i className="fas fa-comment"></i> Kakao</>}
            </button>
            */}
            {/* 네이버 로그인 버튼 주석 처리
            <button 
              className={`btn-social btn-naver ${isLoading ? 'btn-loading' : ''}`}
              onClick={handleNaverLogin}
              disabled={isLoading}
            >
              {!isLoading && <><i className="fas fa-n"></i> Naver</>}
            </button>
            */}
          </div>
        </div>
        
        <div className="login-links">
          <Link to="/forgot-password">비밀번호를 잊으셨나요?</Link>
          <Link to="/register">계정이 없으신가요? 회원가입</Link>
        </div>
      </div>
    </div>
  );
};

export default Login; 